import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import ProjectManager from './components/ProjectManager';
import MainSite from './components/MainSite';
import ProjectPage from './components/ProjectPage';
import AdminSidebar from './components/AdminSidebar';
import AdminHeader from './components/AdminHeader';
import StakeSection from './components/StakeSection';
import LabsAdmin from './components/LabsAdmin'; // NEW VERSION
import BlogSection from './components/BlogSection';
import StakeListPage from './components/StakeListPage';
import StakePage from './components/StakePage';
import BlogPost from './components/BlogPost';
import BlogListPage from './components/BlogListPage';
import LabsPage from './components/LabsPage';
import AboutPage from './components/AboutPage';
import TeamSection from './components/TeamSection';
import InnovationSection from './components/InnovationSection';
import ContactSection from './components/ContactSection';
import StatisticsSection from './components/StatisticsSection';
import TrustedByTeams from './components/TrustedByTeams';
import TrustedByTeamsDisplay from './components/TrustedByTeamsDisplay';
import JobsManager from './components/JobsManager';
import JobsPage from './components/JobsPage';
import BrandKitPage from './components/BrandKitPage';
import { TermsOfUsePage, PrivacyPolicyPage } from './components/LegalPage';
import ErrorBoundary from './components/ErrorBoundary';
import CreateLikeStake from './components/CreateLikeStake';
import CreateInformationPage from './components/CreateInformationPage';
import InformationPageDisplay from './components/InformationPageDisplay';
import CustomPageDisplay from './components/CustomPageDisplay';
import ServicesPage from './components/ServicesPage';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

// Admin Panel Component
interface AdminPanelProps {
  token: string;
  onLogout: () => void;
}

function AdminPanel({ token, onLogout }: AdminPanelProps) {
  const [activeSection, setActiveSection] = useState('main');
  // 🔐 SECURITY: Token is in httpOnly cookie - don't pass as props!
  console.warn(`[AdminPanel] Authenticated: ${token ? '✅' : '❌'} at ${new Date().toISOString()}`);

  const renderContent = () => {
    console.log(`[AdminPanel.renderContent] activeSection: ${activeSection}`);
    switch (activeSection) {
      case 'main':
        return <ProjectManager key="main" token="" defaultCategory="Services" />;
      case 'services':
        return <ProjectManager key="services" token="" defaultCategory="Services" />;
      case 'stake':
        return <StakeSection />;
      case 'labs':
        console.warn(`[AdminPanel.renderContent] Rendering Labs`);
        return <LabsAdmin token="" />;
      case 'blog':
        return <BlogSection />;
      case 'about-team':
        return <TeamSection />;
      case 'about-innovation':
        return <InnovationSection />;
      case 'about-contact':
        return <ContactSection />;
      case 'about-jobs':
        return <JobsManager />;
      case 'about-numbers':
        return <StatisticsSection token="" />;
      case 'about-trusted-teams':
        return <TrustedByTeams token="" />;
      case 'create-like-stake':
        return <CreateLikeStake />;
      case 'create-info-page':
        return <CreateInformationPage />;
      default:
        return <ProjectManager key="services-default" token="" defaultCategory="Services" />;
    }
  };

  return (
    <div className="dusk-admin min-h-screen">
      <AdminHeader onLogout={onLogout} />
      <div className="flex" style={{ height: 'calc(100vh - 73px)' }}>
        <AdminSidebar 
          activeSection={activeSection} 
          onSectionChange={setActiveSection} 
        />
        <div className="flex-1 overflow-y-auto" style={{ background: 'var(--dusk-admin-bg)' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// Routes component that uses useNavigate
function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string>('authenticated'); // Simple flag
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  console.log('🚀 AppRoutes rendering - isAuthenticated:', isAuthenticated, 'token:', token ? '✅' : '❌', 'loading:', loading);

  const tryRefreshSession = async (): Promise<boolean> => {
    try {
      const refreshResponse = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include'
      });
      return refreshResponse.ok;
    } catch (error) {
      console.error('❌ [Session] Refresh request failed:', error);
      return false;
    }
  };

  const checkSession = async (): Promise<{ authenticated: boolean; networkError: boolean }> => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      });

      if (response.ok) {
        return { authenticated: true, networkError: false };
      }

      // If access token expired, attempt refresh and re-check once
      if (response.status === 401 || response.status === 403) {
        const refreshed = await tryRefreshSession();
        if (!refreshed) {
          return { authenticated: false, networkError: false };
        }

        const retryResponse = await fetch('/api/auth/me', {
          credentials: 'include'
        });

        return { authenticated: retryResponse.ok, networkError: false };
      }

      return { authenticated: false, networkError: false };
    } catch (error) {
      console.error('❌ [Session] Auth check network error:', error);
      return { authenticated: false, networkError: true };
    }
  };

  // 🔐 SECURITY: Check if user is authenticated via cookie on mount
  useEffect(() => {
    const checkAuth = async () => {
      const result = await checkSession();

      if (result.authenticated) {
        console.log('✅ User authenticated via cookie');
        setToken('authenticated');
        setIsAuthenticated(true);
      } else if (!result.networkError) {
        console.log('❌ User not authenticated');
        setToken('');
        setIsAuthenticated(false);
        if (window.location.pathname.includes('/admin')) {
          navigate('/login', { replace: true });
        }
      } else {
        console.warn('⚠️ Auth check skipped logout due to transient network error');
      }

      setLoading(false);
    };

    checkAuth();

    // 🔐 SECURITY: Re-check auth when window regains focus
    const handleFocus = async () => {
      console.log('🔍 [WindowFocus] Window regained focus - checking auth...');
      const result = await checkSession();

      if (!result.authenticated && !result.networkError && isAuthenticated) {
        console.warn('⚠️ [WindowFocus] Session lost - logging out');
        setToken('');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [navigate, isAuthenticated]);

  // 🔐 SECURITY: Periodically check if session is still valid (every 10 minutes)
  useEffect(() => {
    if (!isAuthenticated) return;

    const sessionCheckInterval = setInterval(async () => {
      console.log('🔄 [SessionCheck] Checking session validity...');
      const result = await checkSession();

      if (!result.authenticated && !result.networkError) {
        console.warn('⚠️ [SessionCheck] Session invalid - logging out');
        setToken('');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
        return;
      }

      if (result.authenticated) {
        console.log('✅ [SessionCheck] Session still valid');
      }
    }, 10 * 60 * 1000); // Check every 10 minutes

    return () => clearInterval(sessionCheckInterval);
  }, [isAuthenticated, navigate]);

  // 🔐 SECURITY: Logout - clear cookie on backend
  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear cookie
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include' // ✅ Send cookies
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setToken('');
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  // 🔐 SECURITY: After login, cookie is automatically set by backend
  const handleLogin = (newToken: string, refreshToken: string = '') => {
    // newToken is just a flag, real token is in httpOnly cookie
    setToken(newToken || 'authenticated');
    setIsAuthenticated(true);
    // ✅ No need to set localStorage - cookie handles it
    // Redirect to admin panel
    navigate('/admin', { replace: true });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#15161c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#f9fafb' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Routes>
        <Route 
          path="/admin" 
          element={
            isAuthenticated ? (
              <>
                {console.log('🚀 Route /admin matched! isAuthenticated:', isAuthenticated, 'token:', token ? '✅' : '❌')}
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AdminPanel token={token} onLogout={handleLogout} />
                </ProtectedRoute>
              </>
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/login" 
          element={
            <>
              {console.log('🚀 Route /login matched!')}
              {isAuthenticated ? 
                <Navigate to="/admin" replace /> : 
                <Login onLogin={handleLogin} />
              }
            </>
          } 
        />
        <Route path="/" element={<MainSite />} />
        <Route path="/stake" element={<StakeListPage />} />
        <Route path="/stake/:projectUrl" element={<StakePage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/labs" element={<LabsPage />} />
        <Route path="/partners" element={<TrustedByTeamsDisplay />} />
        <Route path="/careers" element={<JobsPage />} />
        <Route path="/brand-kit" element={<BrandKitPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:network/:slug" element={<ProjectPage />} />
        <Route path="/project/:slug" element={<ProjectPage />} />
        <Route path="/page/:slug" element={<InformationPageDisplay />} />
        <Route path="/:pageUrl" element={<CustomPageDisplay />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

// Main App component
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AppRoutes />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
